import * as React from 'react';
import styled from 'styled-components';
import Headline from '@adecco/base-app/src/components/atoms/Headline/Headline';
import Paragraph from '@adecco/base-app/src/components/atoms/Paragraph/Paragraph';
import Button from '@adecco/base-app/src/components/atoms/Button/Button';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import Container from '@adecco/base-app/src/components/atoms/Container/Container';
import DynamicImage from '@adecco/base-app/src/components/atoms/DynamicImage/DynamicImage';
import { MultiMedia } from './MultiMediaElement.types';

type IMultimedia = MultiMedia & {
  centeredHeadline?: boolean;
};

const Wrapper = styled.div<{
  position: 'left' | 'right';
}>`
  ${moreThan('md')} {
    display: flex;
    flex-direction: ${(props) => (props.position === 'left' ? 'row' : 'row-reverse')};
  }
`;

const Media = styled.figure`
  margin-bottom: ${(props) => props.theme.margins.media};

  ${moreThan('md')} {
    display: block;
    width: 40%;
  }
`;

const IframeWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding-top: 56.25%;
  border-radius: ${(props) => props.theme.radius.media};
  box-shadow: ${(props) => props.theme.boxShadow.media};

  ${moreThan('md')} {
    border-radius: ${(props) => props.theme.radius.mediaWideScreen};
    box-shadow: ${(props) => props.theme.boxShadow.mediaWideScreen};
  }
`;

const Iframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
`;

const Image = styled(DynamicImage)`
  max-width: 100%;
  height: auto;
  border-radius: ${(props) => props.theme.radius.media};
  box-shadow: ${(props) =>
    props.theme.brandColor
      ? `0 5px 15px 0px ${props.theme.brandColor}26`
      : props.theme.boxShadow.media};

  ${moreThan('md')} {
    border-radius: ${(props) => props.theme.radius.mediaWideScreen};
    box-shadow: ${(props) =>
      props.theme.brandColor
        ? `0 5px 15px 0px ${props.theme.brandColor}26`
        : props.theme.boxShadow.mediaWideScreen};
  }
`;

const Content = styled.div<{
  position: 'left' | 'right';
}>`
  ${moreThan('md')} {
    width: 60%;
    margin: ${(props) => (props.position === 'left' ? '0 0 0 50px' : '0 50px 0 0')};
  }
`;

const AboveLine = styled(Headline)`
  display: none;
  font-size: ${(props) => props.theme.fontSizes.sublineWideScreen};
  line-height: ${(props) => props.theme.lineHeights.sublineWideScreen};

  ${moreThan('md')} {
    display: block;
  }
`;
const AboveLineXS = styled(Headline)`
  margin-bottom: ${(props) => props.theme.margins.multiMediaAboveline};
  font-size: ${(props) => props.theme.fontSizes.subline};
  line-height: ${(props) => props.theme.lineHeights.subline};

  ${moreThan('md')} {
    display: none;
  }
`;

const StyledHeadlineXs = styled(Headline)`
  font-family: ${(props) => props.theme.fonts.HeadlineBold};
  font-size: ${(props) => props.theme.fontSizes.mediaHeadline};
  line-height: ${(props) => props.theme.lineHeights.headline2};

  ${moreThan('md')} {
    display: none;
  }
`;

const StyledHeadline = styled(Headline)`
  display: none;
  font-size: ${(props) => props.theme.fontSizes.mediaHeadlineWideScreen};
  font-family: ${(props) => props.theme.fonts.HeadlineBold};
  line-height: ${(props) => props.theme.lineHeights.headline2WideScreen};

  ${moreThan('md')} {
    display: block;
  }
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: 20px;
`;

const MultiMediaElement: React.FC<IMultimedia> = ({
  aboveLine,
  headline,
  text,
  media,
  button,
  centeredHeadline,
}) => (
  <Container>
    <Wrapper position={media.position}>
      <AboveLineXS skin={3} centered={centeredHeadline}>
        {aboveLine}
      </AboveLineXS>
      <StyledHeadlineXs skin={2} centered={centeredHeadline}>
        {headline}
      </StyledHeadlineXs>
      <Media>
        {media.type === 'video' ? (
          <IframeWrapper>
            <Iframe
              width="560"
              height="315"
              src={`https://www.youtube.com/embed/${media.src}`}
              title={media.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </IframeWrapper>
        ) : (
          <Image image={media.src} alt={media.title} cloudinaryImage={media.cloudinaryImage} />
        )}
      </Media>
      <Content position={media.position}>
        <AboveLine skin={3}>{aboveLine}</AboveLine>
        <StyledHeadline skin={2}>{headline}</StyledHeadline>
        <StyledParagraph>{text}</StyledParagraph>
        {button.link && button.text && (
          <Button href={button.link} variant="small-ghost">
            {button.text}
          </Button>
        )}
      </Content>
    </Wrapper>
  </Container>
);

export default MultiMediaElement;
